import { TFunction } from 'i18next';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  Link,
  List,
  Text,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { VasError, VasErrorBody } from '../../common/errors/VasError';
import {
  useGetReferringRPId,
  useGetSource,
  useGetUtmSource,
} from '../../common/hooks/useQueryParams';
import { useConsentAndSubmitRbcGicApplicationV2 } from '../../data/rbcGic';
import GicRateDisplay from './GicRateDisplay';
import HandsIcon from './assets/HandsIcon';
import { RbcGicEvents, RbcGicFormTypeV2 } from './types';

interface ConsentFormProps {
  setFormState: React.Dispatch<React.SetStateAction<RbcGicFormTypeV2>>;
  formState: RbcGicFormTypeV2;
  goNext: () => void;
  goBack: () => void;
}

interface ConsentAndAgreementTextProps {
  value: string;
}

const ConsentAndAgreementBoldText = ({ value }: ConsentAndAgreementTextProps) => (
  <Box pt={2}>
    <Text intent="secondary" variant="labelL" align="start">
      {value}
    </Text>
  </Box>
);

const ConsentAndAgreementNormalText = ({ value }: ConsentAndAgreementTextProps) => (
  <Box pt={2}>
    <Text intent="secondary" variant="bodyM" align="start">
      {value}
    </Text>
  </Box>
);

interface ConsentAndAgreementMessageProps {
  t: TFunction;
}

const ConsentAndAgreementMessage = ({ t }: ConsentAndAgreementMessageProps) => (
  <>
    <Text intent="secondary" variant="titleM" align="start">
      {t('rbcv2.consent.rbcAgreementAndConsentHeader')}
    </Text>
    <Box pt={4}>
      <Text intent="secondary" variant="bodyM" align="start">
        {t('rbcv2.consent.rbcAgreementAndConsent')}
      </Text>
    </Box>
    <Box pt={4} pb={4}>
      <List aria-labelledby="ordered-list" ordered>
        <List.Item>
          <ConsentAndAgreementBoldText value={t('rbcv2.consent.rbcAgreementAndConsentItem1')} />
          <ConsentAndAgreementNormalText value={t('rbcv2.consent.rbcAgreementAndConsentItem1-1')} />
        </List.Item>
        <List.Item>
          <ConsentAndAgreementBoldText value={t('rbcv2.consent.rbcAgreementAndConsentItem2')} />
          <ConsentAndAgreementNormalText value={t('rbcv2.consent.rbcAgreementAndConsentItem2-1')} />
        </List.Item>
        <List.Item>
          <ConsentAndAgreementBoldText value={t('rbcv2.consent.rbcAgreementAndConsentItem3')} />
          <ConsentAndAgreementNormalText value={t('rbcv2.consent.rbcAgreementAndConsentItem3-1')} />
        </List.Item>
        <List.Item>
          <ConsentAndAgreementBoldText value={t('rbcv2.consent.rbcAgreementAndConsentItem4')} />
          <ConsentAndAgreementNormalText value={t('rbcv2.consent.rbcAgreementAndConsentItem4-1')} />
        </List.Item>
      </List>
    </Box>
  </>
);

interface CancelButtonProps {
  label: string;
  disabled: boolean;
}

const CancelButton = ({ label, disabled }: CancelButtonProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Dialog size="md" onOpenChange={(open: boolean) => setDialogOpen(open)} open={dialogOpen}>
      <Dialog.Trigger>
        <Button disabled={disabled} emphasis="transparent" intent="primary" size="md" width="hug">
          {label}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content>
        <Flex align="center" justify="center">
          <Text intent="secondary" variant="titleS">
            Cancel Application?
          </Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          intent="primary"
          onClick={() => {
            window.open('gic/rbc/initiate', '_self');
          }}
          width="fill"
        >
          Yes
        </Button>
        <Dialog.CloseButton width="fill">No</Dialog.CloseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

const ConsentForm: React.FC<ConsentFormProps> = ({
  goNext,
  goBack,
  formState,
  setFormState,
}: ConsentFormProps) => {
  const { t } = useTranslation();
  const [gicProviderConsent, setGicProviderConsent] = useState(false);
  const [abConsent, setAbConsent] = useState(false);
  const toasts = useToasts();
  const { isConsentingAndSubmittingApplication, consentAndSubmitRbcGicApplicationV2 } =
    useConsentAndSubmitRbcGicApplicationV2();
  const referrerId = useGetReferringRPId();
  const utmSource = useGetUtmSource();
  const source = useGetSource();
  const { trackEvent } = useEventTracker();
  const isConsented = gicProviderConsent && abConsent;
  const submitDisabled = !isConsented || isConsentingAndSubmittingApplication;

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      <Flex direction="row" align="start" justify="start" pt={24} pl={32}>
        <div
          style={{
            maxWidth: '800px',
          }}
        >
          <Box pl={20} hideAbove="md">
            <HandsIcon />
          </Box>
          <Flex direction="column" align="start">
            <Text intent="primary" variant="headlineL" align="start">
              {t('rbcv2.consent.title')}
            </Text>
            <GicRateDisplay />
            <Box pt={10}>
              <ConsentAndAgreementMessage t={t} />
              <Flex align="start" direction="column">
                <Checkbox
                  required
                  disabled={isConsentingAndSubmittingApplication}
                  helpText=""
                  label={
                    <>
                      <Link
                        href="https://www.applyboard.com/wp-content/uploads/2025/04/113791_IntlStudent_GICProg_ApplicPckg_131232_0425_E_hr.pdf"
                        mode="override_user_choice_and_open_in_new_tab"
                      >
                        {t('rbcv2.consent.disclosureCheck1Line1')}
                      </Link>
                      <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  labelPlacement="right"
                  onChange={(value: boolean) => {
                    setGicProviderConsent(value);
                  }}
                  value={gicProviderConsent}
                />
                <Checkbox
                  required
                  disabled={isConsentingAndSubmittingApplication}
                  helpText=""
                  label={
                    <Text intent="secondary" variant="labelM" align="start">
                      {t('rbcv2.consent.diclosureCheck2-1')}
                      <Link
                        href="https://www.applyboard.com/discover/gic/terms-of-service"
                        mode="override_user_choice_and_open_in_new_tab"
                      >
                        {t('rbcv2.consent.diclosureCheck2-2')}
                      </Link>
                      {t('rbcv2.consent.diclosureCheck2-3')}
                      <span style={{ color: 'red' }}>*</span>
                    </Text>
                  }
                  labelPlacement="right"
                  onChange={(value: boolean) => {
                    setAbConsent(value);
                  }}
                  value={abConsent}
                />
              </Flex>
              <Box pt={16}>
                <Flex direction="column" gap={3}>
                  <Text intent="secondary" variant="bodyS" align="start">
                    {t('rbcv2.consent.disclaimer1')}
                  </Text>{' '}
                  <Text intent="secondary" variant="labelM" align="start">
                    {t('rbcv2.consent.disclaimer2')}
                  </Text>{' '}
                  <Text intent="secondary" variant="bodyS" align="start">
                    {t('rbcv2.consent.disclaimer3')}
                  </Text>
                  <Text intent="secondary" variant="bodyS" align="start">
                    {t('rbcv2.consent.disclaimer4')}
                  </Text>
                </Flex>
              </Box>
              <Box pt={8}>
                <Button
                  intent="primary"
                  size="lg"
                  width="hug"
                  loading={isConsentingAndSubmittingApplication}
                  type="submit"
                  disabled={submitDisabled}
                  onClick={() => {
                    consentAndSubmitRbcGicApplicationV2(
                      {
                        id: formState.id ?? '',
                        verificationCode: formState.verificationCode ?? '',
                      },
                      response => {
                        formState.gicId = response.data.attributes.gic_id;
                        setFormState(formState);
                        toasts.positive(t('common.submissionSuccess') as string);

                        trackEvent({
                          event: RbcGicEvents.SubmissionCompleted,
                          data: { rp_id: referrerId, utm_source: utmSource, source: source },
                        });

                        goNext();
                      },
                      error => {
                        console.error('error: ', error);
                        let errorMessage: string = t('common.submmissionFailed');
                        if (error instanceof VasError) {
                          const vasError: VasError = error;
                          const errorBody: VasErrorBody = vasError.errorBody;
                          errorMessage = vasError.message;
                          if (vasError.errorBody.statusCode === 409) {
                            errorMessage =
                              errorBody.message instanceof Array<string>
                                ? errorBody.message.join(',')
                                : errorBody.message;
                          }
                        }
                        const e = new Error(
                          `RBC GIC Submission API failure. Reason ${errorMessage}`,
                        );
                        trackEvent({
                          event: RbcGicEvents.SubmissionErrored,
                          data: {
                            rp_id: referrerId,
                            utm_source: utmSource,
                            source: source,
                          },
                          error: e,
                        });
                        toasts.negative(new Error(errorMessage));
                      },
                    );
                  }}
                >
                  {t('rbcv2.consent.submitButton')}
                </Button>
              </Box>
            </Box>
          </Flex>
        </div>
        <Box pl={20} hideBelow="lg">
          <HandsIcon />
        </Box>
      </Flex>
      <Box pl={16} pt={8} pb={8}>
        <Flex gap={8}>
          <Button
            disabled={isConsentingAndSubmittingApplication}
            emphasis="transparent"
            intent="primary"
            onClick={goBack}
            size="md"
            type="button"
            width="hug"
          >
            {t('common.goBack')}
          </Button>
          <CancelButton
            label={t('rbcv2.consent.cancelApplication')}
            disabled={isConsentingAndSubmittingApplication}
          />
        </Flex>
      </Box>
    </div>
  );
};

export default ConsentForm;
